import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyMigrationAction,
  applyTenantRunAction,
  getMigration,
  listMigrations,
  listRuns,
  migrationKeys,
} from './migrations-api';
import type {
  MigrationActionFormValues,
  TenantRunActionFormValues,
} from './types';

export function useProfileMigrations() {
  return useQuery({
    queryKey: migrationKeys.list(),
    queryFn: listMigrations,
    staleTime: 15_000,
  });
}

export function useProfileMigration(id: string | undefined) {
  return useQuery({
    queryKey: migrationKeys.detail(id ?? '__none__'),
    queryFn: () => {
      if (!id) throw new Error('migrationId is required');
      return getMigration(id);
    },
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useMigrationRuns(id: string | undefined) {
  return useQuery({
    queryKey: migrationKeys.runs(id ?? '__none__'),
    queryFn: () => {
      if (!id) throw new Error('migrationId is required');
      return listRuns(id);
    },
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useApplyMigrationAction(migrationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: MigrationActionFormValues) =>
      applyMigrationAction(migrationId, values),
    onSuccess: (next) => {
      queryClient.setQueryData(migrationKeys.detail(migrationId), next);
      queryClient.invalidateQueries({ queryKey: migrationKeys.list() });
    },
  });
}

export function useApplyTenantRunAction(migrationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      values,
    }: {
      runId: string;
      values: TenantRunActionFormValues;
    }) => applyTenantRunAction(migrationId, runId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.runs(migrationId) });
    },
  });
}
